import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface GoogleVeo20GeneratePreviewParams {
  /**
   * 视频生成提示词，长度不超过2000字符
   */
  prompt: string;
  /**
   * 输入图像，用于I2V模式，支持URL、base64数据或包含bytesBase64Encoded/gcsUri的对象
   */
  image?: string | any;
  /**
   * 负向提示词
   */
  negativePrompt?: string;
  /**
   * 视频时长（秒），支持5、6、7或8秒，默认5秒
   * @default 5
   * @values 5 | 6 | 7 | 8
   */
  durationSeconds?: 5 | 6 | 7 | 8;
  /**
   * 视频宽高比，支持: 9:16, 16:9，默认16:9
   * @default "16:9"
   * @values 9:16 | 16:9
   */
  aspectRatio?: '9:16' | '16:9';
  /**
   * 是否增强提示词，默认true
   * @default true
   */
  enhancePrompt?: boolean;
  /**
   * 人物生成设置，支持: dont_allow（不允许）, allow_adult（允许成年人），默认allow_adult
   * @default "allow_adult"
   * @values dont_allow | allow_adult
   */
  personGeneration?: 'dont_allow' | 'allow_adult';
  /**
   * 生成样本数量，范围1-4，默认1
   * @default 1
   * @range 1 - 4
   */
  sampleCount?: number;
  /**
   * 随机种子，范围0-4294967295
   * @range 0 - 4294967295
   */
  seed?: number;
  /**
   * 存储URI，用于指定GCS存储位置，默认返回base64编码
   */
  storageUri?: string;
}

export interface GoogleVeo20GeneratePreviewResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * google_veo_20_generate_preview
 *
 * Google Veo 2.0 Generate Preview 视频生成器
 * 
 * Video generation implementation for Google Vertex AI Veo 2.0 Preview API.
 * 
 * Model: veo-2.0-generate-preview
 * Features: 文字生成视频 (T2V) 和图像生成视频 (I2V)
 * Duration: 5-8秒
 * Aspect Ratio: 9:16, 16:9
 * 
 * API Documentation: https://cloud.google.com/vertex-ai/generative-ai/docs/model-reference/veo-video-generation
 *
 * @example
 * ```typescript
 * import { initSeacloud, googleVeo20GeneratePreview } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await googleVeo20GeneratePreview({
 *   // 参数见 GoogleVeo20GeneratePreviewParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 GoogleVeo20GeneratePreviewParams 接口定义
 * @returns 生成的资源列表
 */
export async function googleVeo20GeneratePreview(
  params: GoogleVeo20GeneratePreviewParams,
): Promise<GoogleVeo20GeneratePreviewResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'google_veo_20_generate_preview',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: GoogleVeo20GeneratePreviewResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
